import {
    Fragment,
    forwardRef,
    useEffect,
    useImperativeHandle,
    useState,
    ReactNode,
    ChangeEvent,
    Ref,
    MouseEvent,
} from 'react'
import { useSearchParams } from 'react-router'
import Box from '@mui/material/Box'
import Checkbox from '@mui/material/Checkbox'
import Table from '@mui/material/Table'
import TableSortLabel from '@mui/material/TableSortLabel'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TableCell from '@mui/material/TableCell'
import TableBody from '@mui/material/TableBody'
import TablePagination from '@mui/material/TablePagination'
import Tooltip from '@mui/material/Tooltip'

import { ListResult, SortOrder } from 'common/api/v1/types'
import { Query } from 'common/query'
import { PaginatedDataFetchingFilter, usePaginatedDataFetching } from './hooks/hook-paginated-data-fetching'
import { Loading } from 'common-ui/components/Loading'
import { rowScrollerSx } from 'common-ui/components/Table/Table'
import { ActionMenuRow } from 'common-ui/components/Table/ActionMenuContext'
import { FilterType } from 'common-ui/components/Filter'
import { FilteredListLayout } from 'common-ui/components/Filters/FilteredListLayout'
import { ListSearchBar } from 'common-ui/components/Filters/ListSearchBar'
import type { ListFilterField } from 'common-ui/components/Filters/useListFilters'

export interface Row {
    id: string
}

export interface Column<T extends Row, SortableField> {
    sortField?: SortableField
    title: string
    titleHelp?: string
    valueForColumn: (element: T) => string | ReactNode
    // isClickable defaults to true
    isClickable?: boolean
}

export interface TableProps<T extends Row, SortableField> {
    /** Scroll rows with a pinned header inside a fillHeight page. */
    stickyHeader?: boolean
    isLoading: boolean
    columns: Column<T, SortableField>[]
    rows: T[]
    onRowClicked?: (element: T, index: number) => void
    isRowClickable?: (element: T) => boolean
    onRowsSelected?: (elements: T[]) => void
    onSortRequested?: (sortOrder: { sortField: SortableField; direction: 'asc' | 'desc' }) => void
    // A message to display when there are zero rows
    emptyRowsMessage: string
    renderExpandedRow?: (element: T) => ReactNode
}

export function DataTable<T extends Row, SortableField>(props: TableProps<T, SortableField>) {
    const {
        columns,
        rows,
        onRowClicked,
        isRowClickable,
        onRowsSelected,
        onSortRequested,
        isLoading,
        emptyRowsMessage,
        renderExpandedRow,
        stickyHeader,
    } = props
    const areRowsSelectable = !!onRowsSelected
    const isTableSortable = !!onSortRequested

    const maxColumns = 12
    const hasExtraCheckboxColumn = areRowsSelectable
    const columnWidth = maxColumns / (columns.length + (hasExtraCheckboxColumn ? 1 : 0))

    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc')
    const [sortBy, setSortBy] = useState<SortableField | undefined>(undefined)
    const onSortClicked = (sortField: SortableField) => {
        if (!isTableSortable) return
        const isAsc = sortBy === sortField && sortDirection === 'asc'
        const newDirection = isAsc ? 'desc' : 'asc'
        setSortDirection(newDirection)
        setSortBy(sortField)
        onSortRequested({ sortField, direction: newDirection })
    }

    const [selectedRows, setSelectedRows] = useState<string[]>([])

    useEffect(() => {
        // Rows has changed - remove selected rows that no longer exist
        // E.g. user may have selected a row and then filtered it away
        const existingSelectedRows = selectedRows.filter((selected) => rows.find((row) => row.id === selected))
        updateSelectedRows(existingSelectedRows)
    }, [rows])

    const handleSelectAllClick = (event: ChangeEvent<HTMLInputElement>) => {
        const selectAll = event.target.checked
        const newSelectedRows = selectAll ? rows.map((row) => row.id) : []
        updateSelectedRows(newSelectedRows)
    }
    const handleSelectRowClick = (rowId: string) => {
        const isSelected = selectedRows.includes(rowId)
        const newSelectedRows = isSelected ? selectedRows.filter((id) => id !== rowId) : [...selectedRows, rowId]
        updateSelectedRows(newSelectedRows)
    }

    const updateSelectedRows = (newSelectedRows: string[]) => {
        setSelectedRows(newSelectedRows)
        onRowsSelected?.(rows.filter((row) => newSelectedRows.includes(row.id)))
    }

    return (
        <Box sx={stickyHeader ? rowScrollerSx : { overflowX: 'auto' }}>
            <Table stickyHeader={stickyHeader}>
                <TableHead
                    sx={
                        stickyHeader
                            ? { '& .MuiTableCell-head:not(.MuiTableCell-paddingCheckbox)': { py: 1 } }
                            : undefined
                    }
                >
                    <TableRow>
                        {!isLoading && hasExtraCheckboxColumn && (
                            <TableCell colSpan={1} padding="checkbox">
                                <Checkbox
                                    onChange={handleSelectAllClick}
                                    style={{ cursor: 'default' }}
                                    indeterminate={selectedRows.length > 0 && selectedRows.length < rows.length}
                                    checked={rows.length > 0 && selectedRows.length === rows.length}
                                />
                            </TableCell>
                        )}
                        {!isLoading &&
                            columns.map((column) => {
                                const isColumnSortable = !!column.sortField
                                const title = column.titleHelp ? (
                                    <Tooltip title={column.titleHelp} describeChild>
                                        <Box
                                            component="span"
                                            tabIndex={0}
                                            sx={{ borderBottom: '1px dotted', cursor: 'help' }}
                                        >
                                            {column.title}
                                        </Box>
                                    </Tooltip>
                                ) : (
                                    column.title
                                )
                                return (
                                    <TableCell
                                        key={column.title}
                                        colSpan={columnWidth}
                                        sortDirection={
                                            isTableSortable && isColumnSortable && sortBy === column.sortField
                                                ? sortDirection
                                                : false
                                        }
                                    >
                                        {isTableSortable && isColumnSortable && (
                                            <TableSortLabel
                                                style={{ textDecoration: 'underline' }}
                                                active={sortBy === column.sortField}
                                                direction={sortBy === column.sortField ? sortDirection : 'asc'}
                                                onClick={() => onSortClicked(column.sortField!)}
                                            >
                                                {title}
                                            </TableSortLabel>
                                        )}
                                        {(!isTableSortable || !isColumnSortable) && title}
                                    </TableCell>
                                )
                            })}
                    </TableRow>
                </TableHead>

                <TableBody>
                    {isLoading && (
                        <TableRow>
                            <TableCell colSpan={maxColumns}>
                                <Loading />
                            </TableCell>
                        </TableRow>
                    )}
                    {!isLoading && rows.length === 0 && (
                        <TableRow>
                            <TableCell colSpan={maxColumns}>{emptyRowsMessage}</TableCell>
                        </TableRow>
                    )}
                    {!isLoading &&
                        rows.map((row, index) => {
                            const isRowSelected = selectedRows.includes(row.id)
                            const expandedRow = renderExpandedRow?.(row)
                            return (
                                <Fragment key={row.id}>
                                    <ActionMenuRow data-test-id="table-row" hover selected={isRowSelected}>
                                        {hasExtraCheckboxColumn && (
                                            <TableCell colSpan={1} padding="checkbox">
                                                <Checkbox
                                                    onChange={() => handleSelectRowClick(row.id)}
                                                    checked={isRowSelected}
                                                    style={{ cursor: 'default' }}
                                                />
                                            </TableCell>
                                        )}
                                        {columns.map((column) => {
                                            const isClickable =
                                                (column.isClickable ?? true) &&
                                                !!onRowClicked &&
                                                (isRowClickable?.(row) ?? true)
                                            return (
                                                <TableCell
                                                    key={column.title}
                                                    colSpan={columnWidth}
                                                    onClick={isClickable ? () => onRowClicked?.(row, index) : undefined}
                                                    style={{ cursor: isClickable ? 'pointer' : 'auto' }}
                                                >
                                                    {column.valueForColumn(row)}
                                                </TableCell>
                                            )
                                        })}
                                    </ActionMenuRow>
                                    {expandedRow && (
                                        <TableRow>
                                            <TableCell
                                                colSpan={maxColumns}
                                                sx={{ borderLeft: 3, borderLeftColor: 'divider' }}
                                            >
                                                {expandedRow}
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </Fragment>
                            )
                        })}
                </TableBody>
            </Table>
        </Box>
    )
}

export enum SearchParamKey {
    orderBy = 'orderBy',
    direction = 'direction',
    searchString = 'searchString',
    page = 'page',
    rowsPerPage = 'rowsPerPage',
}

type WithoutParamName<T> = T extends unknown ? Omit<T, 'paramName'> : never
export type FieldFilterConfig<FieldFilter extends { [filterName: string]: string }> = WithoutParamName<
    Extract<
        ListFilterField,
        { type: FilterType.autocomplete | FilterType.datetime | FilterType.select | FilterType.text }
    >
> & {
    filterName: keyof FieldFilter
}

interface DataFetchingPaginatedTableProps<
    Item extends Row,
    SortBy extends string,
    BoolFilter extends { [filterName: string]: boolean },
    FieldFilter extends { [filterName: string]: string } = Record<string, never>,
> {
    /** Pin filters above and pagination below a table that scrolls its own rows. */
    stickyHeader?: boolean
    api: (
        query: Query<PaginatedDataFetchingFilter<BoolFilter, FieldFilter>, SortOrder<SortBy>>
    ) => Promise<ListResult<Item>>
    columns: Column<Item, SortBy>[]
    onRowClicked?: (item: Item, index: number) => void
    isRowClickable?: (item: Item) => boolean
    onRowsSelected?: (items: Item[]) => void
    selectionActions?: ReactNode
    searchParameters?: {
        filterName?: keyof FieldFilter
    }
    // A message to display when there are zero rows
    emptyRowsMessage: string
    renderExpandedRow?: (item: Item) => ReactNode

    /** Boolean query fields; clearing their tokens restores the initial values. */
    boolFilters?: Array<{ filterName: keyof BoolFilter; label: string; initialValue: boolean }>
    fieldFilters?: Array<FieldFilterConfig<FieldFilter>>
}

type SearchParamUpdate = Partial<Record<string, string | boolean | undefined>>
function DataFetchingPaginatedTableComponent<
    Item extends Row,
    SortBy extends string,
    BoolFilter extends { [filterName: string]: boolean },
    FieldFilter extends { [filterName: string]: string } = Record<string, never>,
>(
    {
        api,
        columns,
        onRowClicked,
        isRowClickable,
        onRowsSelected,
        selectionActions,
        boolFilters,
        fieldFilters,
        searchParameters,
        emptyRowsMessage,
        renderExpandedRow,
        stickyHeader,
    }: DataFetchingPaginatedTableProps<Item, SortBy, BoolFilter, FieldFilter>,
    ref: Ref<RefreshableDataFetchingPaginatedTable>
) {
    const [searchParams, setSearchParams] = useSearchParams()
    const updateSearchParams = (entry: SearchParamUpdate) =>
        setSearchParams(
            (currentSearchParams) => {
                const nextSearchParams = new URLSearchParams(currentSearchParams)
                for (const [key, value] of Object.entries(entry)) {
                    if (value !== undefined && value !== '') nextSearchParams.set(key, value.toString())
                    else nextSearchParams.delete(key)
                }
                return nextSearchParams
            },
            { replace: true }
        )

    const rowsPerPageOptions = [25, 50, 100]
    const currentPage = parseInt(searchParams.get(SearchParamKey.page) || '') || 0
    const rowsPerPage = parseInt(searchParams.get(SearchParamKey.rowsPerPage) || '') || rowsPerPageOptions[0]
    const orderByProperty = searchParams.get(SearchParamKey.orderBy) || undefined
    const sortDirection = (searchParams.get(SearchParamKey.direction) || undefined) as 'asc' | 'desc' | undefined

    const isSearchEnabled = !!searchParameters && !searchParameters.filterName
    const searchString = searchParams.get(SearchParamKey.searchString) || ''
    const searchFilter = isSearchEnabled ? searchString : ''

    const boolFiltersFromSearchParams = (boolFilters ?? []).reduce(
        (acc, { filterName, initialValue }) => {
            const strValue = searchParams.get(filterName.toString()) ?? initialValue.toString()
            acc[filterName.toString()] = strValue === 'true'
            return acc
        },
        {} as { [key: string]: boolean }
    )
    const fieldFiltersFromSearchParams = (fieldFilters ?? []).reduce(
        (acc, { filterName }) => {
            const value = searchParams.get(filterName.toString())
            if (value) acc[filterName.toString()] = value
            return acc
        },
        {} as { [key: string]: string }
    ) as FieldFilter

    const { items, total, isFetching, refreshData } = usePaginatedDataFetching({
        api,
        currentPage,
        rowsPerPage,
        sortOrder: orderByProperty
            ? { field: orderByProperty as SortBy, descending: sortDirection === 'desc' }
            : undefined,
        searchString: searchFilter,
        boolFilters: boolFiltersFromSearchParams as BoolFilter,
        fieldFilters: fieldFiltersFromSearchParams,
    })

    useImperativeHandle(ref, () => ({ refreshData }))

    const filterDescriptors: ListFilterField[] = [
        ...(isSearchEnabled
            ? [{ type: FilterType.text as const, label: 'Search', paramName: SearchParamKey.searchString }]
            : []),
        ...(boolFilters ?? []).map(({ filterName, initialValue, label }) => ({
            defaultValue: initialValue,
            label,
            labelPlacement: 'start' as const,
            paramName: filterName.toString(),
            type: FilterType.checkbox as const,
        })),
        ...(fieldFilters ?? []).map(({ filterName, ...filter }) => ({
            ...filter,
            ...(filter.type === FilterType.select ? { emptyOptionLabel: 'All' } : {}),
            paramName: filterName.toString(),
        })),
    ]
    const pagination = (
        <TablePagination
            component="div"
            sx={
                stickyHeader
                    ? {
                          flexShrink: 0,
                          position: 'sticky',
                          bottom: 0,
                          zIndex: 2,
                          backgroundColor: 'background.paper',
                          borderTop: 1,
                          borderColor: 'divider',
                          '& .MuiTablePagination-toolbar': { px: { xs: 1, sm: 2 } },
                          '& .MuiTablePagination-input': { mr: { xs: 1, sm: 4 } },
                          '& .MuiTablePagination-actions': { ml: { xs: 1, sm: 2.5 } },
                      }
                    : undefined
            }
            count={total}
            page={currentPage}
            rowsPerPageOptions={rowsPerPageOptions}
            rowsPerPage={rowsPerPage}
            onPageChange={(event: MouseEvent<HTMLButtonElement> | null, page: number) =>
                updateSearchParams({ [SearchParamKey.page]: page.toString() })
            }
            onRowsPerPageChange={(event: ChangeEvent<HTMLInputElement>) => {
                updateSearchParams({
                    [SearchParamKey.page]: '0',
                    [SearchParamKey.rowsPerPage]: event.target.value,
                })
            }}
        />
    )

    return (
        <FilteredListLayout
            toolbar={
                filterDescriptors.length > 0 || selectionActions ? (
                    <>
                        {filterDescriptors.length > 0 && (
                            <ListSearchBar
                                fields={filterDescriptors}
                                rawTextParam={
                                    isSearchEnabled
                                        ? SearchParamKey.searchString
                                        : searchParameters?.filterName?.toString()
                                }
                                pageParamName={SearchParamKey.page}
                            />
                        )}
                        {selectionActions}
                    </>
                ) : undefined
            }
        >
            <DataTable
                stickyHeader={stickyHeader}
                isLoading={isFetching}
                columns={columns}
                rows={items}
                emptyRowsMessage={emptyRowsMessage}
                onRowClicked={onRowClicked}
                isRowClickable={isRowClickable}
                onRowsSelected={onRowsSelected}
                renderExpandedRow={renderExpandedRow}
                onSortRequested={({ sortField, direction }) =>
                    updateSearchParams({
                        [SearchParamKey.orderBy]: sortField.toString(),
                        [SearchParamKey.direction]: direction,
                    })
                }
            />
            {pagination}
        </FilteredListLayout>
    )
}

export type RefreshableDataFetchingPaginatedTable = { refreshData: () => void }
const ReferencedComponent = forwardRef(DataFetchingPaginatedTableComponent)
export const DataFetchingPaginatedTable = <
    Item extends Row,
    SortBy extends string,
    BoolFilter extends { [filterName: string]: boolean },
    FieldFilter extends { [filterName: string]: string } = Record<string, never>,
>({
    myRef,
    ...rest
}: DataFetchingPaginatedTableProps<Item, SortBy, BoolFilter, FieldFilter> & {
    myRef: Ref<RefreshableDataFetchingPaginatedTable>
}) => <ReferencedComponent {...(rest as any)} ref={myRef} />
